import '../styles/pages/home-sport.module.scss';
import '../styles/pages/league-feed.module.scss';
import { serializeJsonLd } from "../shared/utils/seo";
import { toSchemaDate } from '../shared/utils/seo';
import { trackConversion } from '../lib/conversionEvents';
import React from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import BaseHead from '../components/Base/BaseHead';
import { Trans, useTranslation } from 'react-i18next';
import {
  AppleFilled,
  AndroidFilled,
  ArrowRightOutlined,
  PlayCircleFilled,
} from '@ant-design/icons';
import { GetServerSidePropsContext } from 'next';
import nookies from 'nookies';
import ContentBox from '../components/Base/ContentBox';
import ButtonBox from '../components/Base/ButtonBox';
import NewsRow from '../components/home/NewsRow';
import HomeDashboard from '../components/home/HomeDashboard';
import AndroidAppWelcome from '../components/home/AndroidAppWelcome';
import HomeLeagues, { HomeLeague } from '../components/home/HomeLeagues';
import { homeTopContentBox } from '../shared/utils/home';
import { compareBySeason, isSeasonOngoing } from '../shared/utils/leagues';
import { convertTitleToUrl } from '../shared/utils/url';
import { News } from '../shared/models/news';
import {
  getLeagues,
  getMatchStatistic,
  getNews,
  getUserByUid,
  getUserStatistic,
} from '../lib/firestoreAdmin';
import { getEurosportNews } from '../lib/eurosportNews';
import SponsorsPartners from '../components/Base/SponsorsPartners';
import { USERS_COLLECTION } from '../shared/variables/collections';
import { adminAuth, adminFirestore } from '../lib/firebaseAdmin';
import { APP_STORE_URL, PLAY_STORE_URL } from '../shared/variables/constants';
import {
  ROUTE_LEAGUES,
  ROUTE_NEWS,
  ROUTE_PLAY,
  ROUTE_REGISTER,
  ROUTE_RULES,
} from '../shared/variables/routes';
import { useAppSelector } from '../store/hooks';
import { selectAuthReady, selectUser } from '../store/slices/userSlice';

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const cookies = nookies.get(ctx);
  // `signed_in` yalnız düzen ipucudur (lib/authSession.ts); yine de yanıt kişiye göre değiştiği için
  // paylaşımlı önbelleğe girmez.
  const isPersonalizedRequest = Boolean(cookies.token || cookies.signed_in || ctx.query.playerID);

  // Googlebot ve oturum açmamış ziyaretçiler aynı herkese açık içeriği görür;
  // bu yanıtı edge'de kısa süreli cache'lemek ana sayfanın TTFB'sini düşürür.
  // Token/playerID bulunan istekler kullanıcıya özel yan etki taşıdığı için
  // hiçbir zaman paylaşımlı cache'e girmez.
  ctx.res.setHeader(
    'Cache-Control',
    isPersonalizedRequest
      ? 'private, no-cache, max-age=0, must-revalidate'
      : 'public, s-maxage=300, stale-while-revalidate=3600',
  );

  // Oturum çerezi doğrulanan ya da girişli ipucu taşıyan istek panoyla gelir; istemci oturumu
  // çözülünce bayrağın yerini o alır (oturum yoksa hero'ya döner).
  let signedIn = cookies.signed_in === '1';
  try {
    const query = ctx.query;

    if (cookies.token) {
      const token = await adminAuth.verifyIdToken(cookies.token);
      const { uid } = token;
      signedIn = true;

      const user = (await getUserByUid(uid))[0];

      if (user && query.playerID && user.onesignal_id !== query.playerID) {
        await adminFirestore
          .collection(USERS_COLLECTION)
          .doc(user.id)
          .update({ onesignal_id: query.playerID });
      }
    }
  } catch (e) {
    console.log('e:', e);
  }

  try {
    const [firestoreResult, externalResult, usersResult, matchesResult, leaguesResult] = await Promise.allSettled([
      getNews(),
      getEurosportNews(10),
      getUserStatistic(),
      getMatchStatistic(),
      getLeagues(),
    ]);
    const firestoreNews = firestoreResult.status === 'fulfilled' ? firestoreResult.value : [];
    const eurosportNews = externalResult.status === 'fulfilled' ? externalResult.value : [];
    const userStatistic = usersResult.status === 'fulfilled' ? usersResult.value : null;
    const matchStatistic = matchesResult.status === 'fulfilled' ? matchesResult.value : null;
    // Home league cards: busiest leagues first (members, then season); only the fields the card shows.
    const now = Date.now();
    const leagues: HomeLeague[] = (leaguesResult.status === 'fulfilled' ? leaguesResult.value : [])
      // Parent placeholders and internal test leagues never belong on the public front page.
      .filter(league => league.lig_tipi !== 'ana' && !/\btest\b/i.test(league.lig_ad || ''))
      .sort((a, b) => (b.uye_sayisi || 0) - (a.uye_sayisi || 0) || compareBySeason(a, b, now))
      .slice(0, 6)
      .map(league => ({
        id: league.id,
        name: league.lig_ad,
        city: league.il_ad || '',
        logo: league.lig_logo_url || null,
        members: league.uye_sayisi || 0,
        url: convertTitleToUrl('ligler', league.lig_ad, league.id),
        ongoing: isSeasonOngoing(league, now),
      }));
    const news = [...eurosportNews, ...firestoreNews].sort(
      (a: any, b: any) => Date.parse(toSchemaDate(b.haber_tarih) || '1970-01-01') - Date.parse(toSchemaDate(a.haber_tarih) || '1970-01-01'),
    );

    return {
      props: {
        news: news.slice(0, 4),
        leagues,
        usersCount: userStatistic?.kullanici_sayisi || 0,
        matchCount: matchStatistic?.mac_sayisi || 0,
        signedIn,
      },
    };
  } catch (e) {
    return {
      props: {
        news: [],
        leagues: [],
        usersCount: 0,
        matchCount: 0,
        signedIn,
      },
    };
  }
}

export default function Home({
  news,
  leagues,
  usersCount,
  matchCount,
  signedIn,
}: {
  news: News.News[];
  leagues: HomeLeague[];
  usersCount: number;
  matchCount: number;
  signedIn: boolean;
}) {
  const { t } = useTranslation('home');
  const hasStats = usersCount > 0;
  const currentUser = useAppSelector(selectUser);
  const isLoggedIn = !!currentUser?.uid;
  const authReady = useAppSelector(selectAuthReady);
  // Önbellekten gelen misafir sayfasında da ipucu çerezi hidrasyondan hemen sonra okunur;
  // Firebase oturumunun çözülmesi beklenmez.
  const [signedInHint, setSignedInHint] = React.useState(false);
  React.useEffect(() => {
    setSignedInHint(/(?:^|;\s*)signed_in=1(?:;|$)/.test(document.cookie));
  }, []);
  // Giriş yapmış oyuncu pazarlama bandı yerine kişisel panoyu görür (LG-54).
  const showDashboard = authReady ? isLoggedIn : signedIn || signedInHint;

  return (
    <>
      <BaseHead
        pageTitle="Türkiye'nin Tenis Ligi Platformu — Tenis Oyna, Partner Bul, Kort Seç"
        description={`Türkiye'nin tenis platformu. ${hasStats ? `${usersCount.toLocaleString('tr-TR')} tenisçi, ${matchCount.toLocaleString('tr-TR')} maç. ` : ''}Tenis liglerini, kortları ve turnuvaları keşfet. Seviyene uygun lige katıl.`}
      />
      <Head>
        {/* The hero photo is hidden below 768px (styles/components/_mobile.scss); phones must not fetch it first. */}
        {!showDashboard && (
          <link rel="preload" as="image" href="/images/hero-court.svg" media="(min-width: 768px)" key="hero-preload" />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'SportsOrganization',
                  name: 'tenis.io',
                  url: 'https://tenis.io',
                  logo: 'https://tenis.io/images/logo.png',
                  description: "Türkiye genelinde tenis ligleri, kortlar, kulüpler ve turnuvalar için tenis platformu.",
                  sport: 'Tennis',
                  areaServed: { '@type': 'Country', name: 'Türkiye', identifier: 'TR' },
                  sameAs: [
                    'https://www.facebook.com/tenisioapp',
                    'https://twitter.com/tenisioapp',
                    'https://www.instagram.com/tenisioapp/',
                    'https://apps.apple.com/tt/app/tenis-io/id1582320874',
                    'https://play.google.com/store/apps/details?id=com.tenisturnuvasi',
                  ],
                },
                {
                  '@type': 'WebSite',
                  name: 'tenis.io',
                  alternateName: ['Tenis.io', 'tenisio'],
                  url: 'https://tenis.io',
                },
              ],
            }),
          }}
        />
      </Head>

      <div className="home-sport">
        <div className="container">
          <AndroidAppWelcome />
        </div>
        {showDashboard ? <HomeDashboard /> : (
        <section className="home-sport__hero">
          <div className="home-sport__hero-texture" aria-hidden="true" />
          <div className="container home-sport__hero-grid">
            <div className="home-sport__hero-copy">
              {hasStats && (
                <div className="home-sport__hero-rating">
                  <span className="home-sport__hero-rating-text">
                    {t('hero.rating', { userCount: usersCount.toLocaleString('tr-TR') })}
                  </span>
                </div>
              )}
              <h1>
                <Trans i18nKey="hero.title" ns="home" components={{ em: <em /> }} />
              </h1>
              <p>{t('hero.subtitle')}</p>
              <div className="home-sport__hero-actions">
                <Link href={isLoggedIn ? ROUTE_PLAY : ROUTE_LEAGUES}>
                  <a className="home-sport__cta home-sport__cta--primary" onClick={() => { if (!isLoggedIn) trackConversion('league_discovery_started'); }}>
                    {isLoggedIn ? t('hero.ctaPrimaryLoggedIn') : t('hero.ctaPrimaryGuest')}
                  </a>
                </Link>
                <Link href={ROUTE_RULES}>
                  <a className="home-sport__cta home-sport__cta--demo">
                    <PlayCircleFilled /> {t('hero.ctaDemo')}
                  </a>
                </Link>
              </div>
              {hasStats && (
                <div className="home-sport__hero-stats">
                  <div>
                    <strong>{usersCount.toLocaleString('tr-TR')}+</strong>
                    <span>{t('hero.stats.players')}</span>
                  </div>
                  <div>
                    <strong>{matchCount.toLocaleString('tr-TR')}+</strong>
                    <span>{t('hero.stats.matches')}</span>
                  </div>
                  <div>
                    <strong>{(matchCount * 60).toLocaleString('tr-TR')}</strong>
                    <span>{t('hero.stats.minutes')}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="home-sport__hero-media" aria-hidden="true">
              <div className="home-sport__hero-photo-frame">
                <Image
                  className="home-sport__hero-photo"
                  src="/images/hero-court.svg"
                  alt=""
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center top"
                  sizes="(max-width: 575px) 280px, (max-width: 991px) 360px, 480px"
                  loading="eager"
                />
              </div>
              <span className="home-sport__hero-ball home-sport__hero-ball--1" />
              <span className="home-sport__hero-ball home-sport__hero-ball--2" />
              <span className="home-sport__hero-ball home-sport__hero-ball--3" />
              <svg
                className="home-sport__hero-trail"
                viewBox="0 0 400 400"
                preserveAspectRatio="none"
              >
                <path
                  d="M40,360 C80,180 240,40 380,80"
                  fill="none"
                  stroke="rgba(212,164,55,0.45)"
                  strokeWidth="1.5"
                  strokeDasharray="4 8"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </section>
        )}

        <HomeLeagues leagues={leagues} />

        {!showDashboard && (
        <section className="home-sport__journey">
          <div className="container">
            <div className="home-sport__section-head">
              <span>{t('journey.eyebrow')}</span>
              <h2>{t('journey.title')}</h2>
            </div>
            <ContentBox content={homeTopContentBox} ordered />
          </div>
        </section>
        )}

        <section className="home-sport__quick">
          <div className="container">
            <div className="home-sport__section-head">
              <span>{t('quick.eyebrow')}</span>
              <h2>{t('quick.title')}</h2>
            </div>
            <ButtonBox />
          </div>
        </section>

        <section className="home-sport__news" id="news">
          <div className="container">
            <div className="home-sport__section-head home-sport__section-head--with-link">
              <div>
                <span>{t('news.eyebrow')}</span>
                <h2>{t('news.title')}</h2>
              </div>
              {news.length > 0 && (
                <Link href={ROUTE_NEWS}>
                  <a className="home-sport__section-link">
                    {t('news.seeAll')} <ArrowRightOutlined />
                  </a>
                </Link>
              )}
            </div>
            <div className="news mb-lg">
              {news.map(item => (
                <NewsRow key={item.id} news={item} />
              ))}
            </div>
          </div>
        </section>

        <section className="home-sport__apps">
          <div className="container">
            <div className="home-sport__apps-card">
              <div>
                <span>{t('apps.eyebrow')}</span>
                <h2>{t('apps.title')}</h2>
              </div>
              <div className="home-sport__apps-links">
                <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
                  <span className="home-sport__apps-platform">
                    <AppleFilled /> iOS
                  </span>
                  <img src="/images/app-store.svg" alt={t('apps.iosAriaLabel')} width={203} height={60} />
                </a>
                <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
                  <span className="home-sport__apps-platform">
                    <AndroidFilled /> Android
                  </span>
                  <img src="/images/google-play.svg" alt={t('apps.androidAriaLabel')} width={203} height={66} />
                </a>
              </div>
            </div>
          </div>
        </section>

        <SponsorsPartners pageType="HOME" />

      </div>
    </>
  );
}
